import { Component, createApp, h, type ComponentPublicInstance } from 'vue';

// PRODUCT-26110 Phase 2B2: the previous `import VueRouter from 'vue-router'` (used
// only for `Vue.use(this.options.VueRouter)` / `Vue.use(this.options.Vuex)`, vue-router
// 3 + vuex 3's global-plugin-install pattern) does not exist under vue-router 4 at all
// -- v4 has named exports only, no default. Both installs are removed outright: v4 /
// vuex 4 install per-*instance* via `app.use(router)` / `app.use(store)` on a real
// `createApp()` app (see mount(), and dashboard-commons/app/internals/Application.ts
// for the identical restructure that this ports). `Router`/`Route` -> vue-router 4's
// real named exports.
import type { Router, RouteLocationNormalized } from 'vue-router';

import { createPinia, type Pinia } from 'pinia';

import Logger from '@evidentid/logger';
import Tracker from '@evidentid/tracker';

import ApplicationObserver from './ApplicationObserver';

export interface ApplicationOptions<
    L extends Logger<any> = Logger<any>,
    T extends Tracker<any> = Tracker<any>,
    R extends Router = Router,
> {
    location: Location;
    logger: L;
    tracker: T;
    createRouter: (app: any) => R;
}

type RequiredObject<T> = { [K in keyof T]-?: Exclude<T[K], undefined>; };

export abstract class Application<
    L extends Logger<any> = Logger<any>,
    T extends Tracker<any> = Tracker<any>,
    R extends Router = Router,
    O extends ApplicationOptions<L, T, R> = ApplicationOptions<L, T, R>,
> {
    protected abstract readonly Component: Component;
    protected readonly options: RequiredObject<O>;
    protected vue: ComponentPublicInstance | null = null;
    protected root: Element | null = null;
    // PRODUCT-26110 Phase 2B2: holds the real Vue 3 `createApp()` instance — needed
    // separately from `this.vue` (the mounted component instance) since unmounting
    // under Vue 3 is `app.unmount()`, not a method on the component instance itself
    // (Vue 2's `.$destroy()`, which no longer exists). See mount()/
    // destroyMountedComponent().
    private vueApp: ReturnType<typeof createApp> | null = null;
    // PRODUCT-26110 Phase 2B2: vue-router 4's beforeEach/afterEach/beforeResolve return
    // an unregister function instead of exposing mutable `afterHooks`/`beforeHooks`/
    // `resolveHooks` arrays (vue-router-3-only internals) — captured here so destroy()
    // can undo the `router.afterEach(...)` subscription below (matches dashboard-
    // commons/app/internals/Application.ts's identical fix).
    private unregisterRouteChangeListener: (() => void) | null = null;
    public readonly logger: L;
    public readonly tracker: T;
    public readonly observer: ApplicationObserver<any, any, any>;
    public router: R | null = null;
    // PRODUCT-26110 Pinia migration: Pinia replaces the Vuex store; stores are
    // instantiated lazily against this instance (e.g. useIdoStore(app.pinia)).
    public readonly pinia: Pinia = createPinia();

    public constructor(options: O) {
        this.options = Object.assign({}, options) as RequiredObject<O>;
        this.logger = options.logger;
        this.tracker = options.tracker;
        this.observer = new ApplicationObserver<any, any, any>(this as any);
    }

    public get initialized(): boolean {
        return this.router !== null;
    }

    public get mounted(): boolean {
        return this.initialized && this.root !== null;
    }

    public readonly initialize = (): void => {
        if (!this.initialized) {
            // PRODUCT-26110 Phase 2B2: vue-router 4 / vuex 4 install via `app.use()`
            // only, once a real `createApp()` instance exists (see mount() below)
            // (matches dashboard-commons/app/internals/Application.ts's identical
            // restructure).

            // Start initialization
            this.beforeInitialize();

            // Initialize router
            this.router = this.options.createRouter(this);

            // Handle basic logic after router change.
            // PRODUCT-26110 Phase 2B2: vue-router 4 fires afterEach on aborted/cancelled/
            // duplicated navigations too; skip those so onRouteChange only sees real
            // navigations (matches syncRouteToStore + dashboard-commons Application).
            this.unregisterRouteChangeListener = this.router!.afterEach(
                (to, from, failure) => (failure ? undefined : this.onRouteChange(to, from)),
            );

            // PRODUCT-26110 Task 14: Vue error handling is now set up in mount(),
            // against the real per-instance createApp() app -- see there for why (the
            // legacy `Vue.config.errorHandler` global-singleton path this used to take
            // required @vue/compat, now removed).

            // Finish initialization
            this.afterInitialize();
        }
    };

    public readonly destroy = (): void => {
        if (this.initialized) {
            // Start destroy
            this.beforeDestroy();

            // Destroy Vue
            this.destroyMountedComponent();

            // Unsubscribe from router: vue-router 4 has no `afterHooks`/
            // `beforeHooks`/`resolveHooks` arrays (vue-router-3-only internals) --
            // v4's beforeEach/afterEach/beforeResolve each return an unregister
            // function instead, captured in initialize() and called here.
            if (this.unregisterRouteChangeListener) {
                this.unregisterRouteChangeListener();
                this.unregisterRouteChangeListener = null;
            }

            // Remove references
            this.vue = null;
            this.router = null;

            // Finish destroy
            this.afterDestroy();
        }
    };

    public readonly mount = (root: Element): void => {
        if (!this.initialized) {
            throw new Error('You can\'t render already destroyed application instance.');
        } else if (this.mounted) {
            this.destroyMountedComponent();
        }

        this.root = root;
        this.beforeMount();
        // PRODUCT-26110 Phase 2B2: `router`/`store` as root options to `new Vue(...)`
        // was vue-router-2/3 + vuex-2/3's mixin-based install (via their `Vue.use()`
        // global registration); vue-router 4 / vuex 4 install per-instance via
        // `app.use(router)` / `app.use(store)` on a real Vue 3 app instead --
        // construction order matches dashboard-commons/app/internals/Application.ts's
        // mount(): createApp() first, then app.use(store)/app.use(router), then
        // app.mount().
        const app = createApp({ render: () => h(this.Component) });
        app.use(this.pinia);
        app.use(this.router!);

        // PRODUCT-26110 Task 14: the legacy `Vue.config.errorHandler` global-singleton
        // assignment (previously set in initialize(), before the app instance existed,
        // via a compat-mode cast on the module's default import) moves here onto the
        // real per-instance app -- Vue 3 error handling is app-scoped, not global. A
        // freshly created app always starts with `app.config.errorHandler === undefined`, so there's no
        // prior handler left to preserve/chain (the old chaining logic is now moot,
        // not just dead).
        app.config.errorHandler = (error) => {
            this.logger?.warning(error as Error);
            this.onVueError(error as Error);
        };

        this.installAppPlugins(app);

        this.vueApp = app;
        this.vue = app.mount(root);
        this.afterMount();
    };

    /* eslint-disable @typescript-eslint/no-empty-function */
    protected beforeMount(): void {}
    protected afterMount(): void {}
    protected beforeInitialize(): void {}
    protected afterInitialize(): void {}
    protected beforeDestroy(): void {}
    protected afterDestroy(): void {}
    protected onVueError(error: Error): void {}
    protected onRouteChange(to: RouteLocationNormalized, from: RouteLocationNormalized): void {}
    // PRODUCT-26110 Task 14: extension point for subclasses that need to register a
    // real Vue 3 plugin (`app.use(...)`) against the mounted app instance -- mirrors
    // dashboard-commons/app/internals/Application.ts's `appPlugins` array for the same
    // purpose. Replaces the legacy `(this.options.Vue as any).use(...)` pattern
    // (IdoFacingApplication's Modal plugin) that only worked via @vue/compat's global
    // singleton.
    protected installAppPlugins(app: ReturnType<typeof createApp>): void {}
    /* eslint-enable */

    private destroyMountedComponent(): void {
        // Get back previous element
        const element = this.vue?.$el;
        if (element?.parentNode) {
            element.parentNode.replaceChild(this.root!, element);
        }

        // Destroy Vue app instance: createApp().unmount() replaces Vue 2's
        // `new Vue(...).$destroy()` under Vue 3 -- the mounted component instance
        // itself (this.vue) has no $destroy() method any more.
        this.vueApp?.unmount();
        this.vueApp = null;
    }
}
